/**
 * The one From Assets control on a phone and an iPad, in the Season design: a sheet with the same groups as the Assets
 * page and a grid of tick circles, narrowed to what fits the place, and one button at the foot. A phone shows the groups
 * first and a group as a grid after it; an iPad is a card with the groups at the left. Every caller gets this one, and
 * the store's picker state is the same the computer's control reads.
 */
import { useState } from "react";
import type { LibraryAsset } from "../../shared/types";
import { Empty } from "../components/season/Empty";
import type { Layout } from "../components/season/layout";
import { Search } from "../components/season/Search";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import "../styles/season-assets.css";
import { ALL, bytesLabel, countIn, filesUnder, folderName, listFiles } from "./library";
import { UpFoot, upLine, upSummary } from "./parts";
import { Grid, Groups, Tile } from "./seasonParts";
import { closePicker, loadLibrary, type PickerState, pickSelected, updatePicker, uploadsAsAssets } from "./store";
import { useAssetsData } from "./useAssets";
import { useRailFold } from "./useRailFold";


export function SeasonPicker({ p, layout }: { p: PickerState; layout: Layout }) {
  const { lib, blind, folders, shown } = useAssetsData();
  const phone = layout === "phone";
  // Why a phone starts on the groups: a place that names a folder (`at`) opens straight into it.
  const [groups, setGroups] = useState(phone && p.folder === ALL);
  const pending = lib.uploads.filter((u) => u.from === "pick");
  const ups = new Map(pending.map((u) => [u.id, u]));
  const all = [...uploadsAsAssets(pending), ...shown];
  const going = upSummary(pending, folderName(folders, pending[pending.length - 1]?.folderId ?? null));
  const first = new Set([...pending.map((u) => u.id), ...lib.fresh]);
  const fitting = all.filter((a) => p.fit.test(a));
  const searching = !!p.q.trim();
  const files: LibraryAsset[] = searching
    ? listFiles(folders, all, { mode: "pick", folder: ALL, q: p.q, fit: p.fit, all: p.all }, first)
    : filesUnder(folders, fitting, p.folder, first);
  const chosen = shown.find((x) => x.id === p.sel) ?? null;
  const ok = !!chosen && p.fit.test(chosen);
  const fold = useRailFold(folders, all, p.q, p.folder);
  const open = (id: string) => {
    updatePicker({ folder: id, q: "" });
    setGroups(false);
  };
  const rail = (
    <Groups folders={folders} assets={all} open={groups ? null : p.folder} fold={fold} blind={blind} hideEmpty count={(id) => countIn(folders, fitting, id)} onOpen={open} />
  );
  const tile = (a: LibraryAsset) => {
    const u = ups.get(a.id);
    if (u) return <Tile key={a.id} a={a} sub="" up={{ line: upLine(u, folderName(folders, u.folderId)), pct: u.stage === 0 ? u.pct : 100, fail: !!u.error }} />;
    const sub = searching || p.folder === ALL ? folderName(folders, a.folder_id) : bytesLabel(a.size_bytes);
    return <Tile key={a.id} a={a} sub={sub} tick on={p.sel === a.id} onClick={() => updatePicker({ sel: p.sel === a.id ? null : a.id, auto: null })} />;
  };
  const grid = lib.status === "loading" && !shown.length ? (
    <p className="k-sub" aria-busy="true">
      Loading
    </p>
  ) : lib.status === "error" ? (
    <Empty title={lib.error ?? "We couldn’t open Assets."}>
      <Button onClick={() => void loadLibrary(true)}>Try again</Button>
    </Empty>
  ) : files.length ? (
    <Grid>{files.map(tile)}</Grid>
  ) : (
    <Empty title="Nothing fits here" />
  );
  const fit = p.fit.label ? (
    <p className="sa-fit">
      <span className="pill wine">Only {p.fit.label}</span>
      {p.fit.hint ? <span className="sa-fit-h"> {p.fit.hint}</span> : null}
    </p>
  ) : null;
  const search = <Search placeholder="Search" label="Search Assets" initial={p.q} onSearch={(q) => updatePicker({ q })} />;

  const body = phone ? (
    <div className="sa-pk">
      {search}
      {searching || !groups ? (
        <>
          {searching ? null : (
            <button type="button" className="sa-pk-b" onClick={() => setGroups(true)}>
              <Icon name="left" />
              <span>{p.folder === ALL ? "All files" : folderName(folders, p.folder)}</span>
            </button>
          )}
          {grid}
        </>
      ) : (
        <>
          {fit}
          {rail}
        </>
      )}
    </div>
  ) : (
    <div className="sa-pk sa-pk2">
      {search}
      {fit}
      <div className="sa-pk-c">
        <div className="sa-pk-r">{rail}</div>
        <div>{grid}</div>
      </div>
    </div>
  );

  return (
    <Sheet
      open
      wide
      onClose={() => closePicker()}
      title={p.title}
      sub={p.sub || undefined}
      footer={
        <div className="sa-foot sa-pkf">
          {going && !chosen ? <UpFoot s={going} /> : null}
          {p.error ? (
            <p className="sa-pk-err" role="alert">
              {p.error}
            </p>
          ) : null}
          <Button variant="p" icon={p.using ? undefined : "check"} disabled={!ok || !!p.using} onClick={() => pickSelected()}>
            {p.using ? (
              <>
                <span className="spin" aria-hidden="true" /> Getting it ready
              </>
            ) : (
              p.cta
            )}
          </Button>
        </div>
      }
    >
      {body}
    </Sheet>
  );
}
